import { UseFormReturn, UseFormUnregister } from "react-hook-form";
import {
  NewExamField,
  FormValues,
  ExtractedExam,
  ExtractedAnalyte,
} from "./types";
import { useMedicalCareContext } from "@/contexts/MedicalCareContext";
import { useSessionContext } from "@/contexts/SessionContext";
import * as uuid from "uuid";
import dayjs from "dayjs";

interface UseExamManagementProps {
  control: UseFormReturn<FormValues>;
  unregister: UseFormUnregister<FormValues>;
  incrementInputKey: () => void;
  setSelectedAnalyte: (value: string | null) => void;
  resetStates: () => void;
}

export const useExamManagement = ({
  control,
  unregister,
  incrementInputKey,
  setSelectedAnalyte,
  resetStates,
}: UseExamManagementProps) => {
  const { user } = useSessionContext();
  const { newExams, setNewExams, setValue } = useMedicalCareContext();

  const handleAddInput = async () => {
    try {
      const newInput = {
        nameId: `newExames.${newExams.length}.name`,
        valueId: `newExames.${newExams.length}.value`,
        dateId: `newExames.${newExams.length}.date`,
        unitId: `newExames.${newExams.length}.unit`,
      };
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simula processamento
      setNewExams((prevExams: Array<NewExamField | string>) => [
        ...prevExams,
        newInput,
      ]);
      incrementInputKey();

      const newAnalyteName = control.getValues(
        `newExames.${newExams.length}.name`
      );
      if (newAnalyteName) {
        setSelectedAnalyte(newAnalyteName);
      }
    } finally {
      resetStates();
    }
  };

  const handleAddExamForAnalyte = async (analyteName: string | null) => {
    try {
      const currentLength = newExams.length;

      const newInput = {
        nameId: `newExames.${currentLength}.name`,
        valueId: `newExames.${currentLength}.value`,
        dateId: `newExames.${currentLength}.date`,
        unitId: `newExames.${currentLength}.unit`,
      };

      setNewExams((prevExams: Array<NewExamField | string>) => [
        ...prevExams,
        newInput,
      ]);

      const formValue = control.getValues(`newExames.${currentLength}`);

      if (formValue?.value && analyteName) {
        setSelectedAnalyte(analyteName);
      }
    } finally {
      incrementInputKey();
      resetStates();
    }
  };

  const handleDeleteResult = (examId: string) => {
    const indexToDelete = examId.split("-")[0];

    if (indexToDelete) {
      setNewExams((prevExams: Array<NewExamField | string>) => {
        const updatedExams = [...prevExams];
        updatedExams[Number(indexToDelete)] = "";
        return updatedExams;
      });
      unregister(`newExames.${examId}`);
    }
  };

  const formatNewExam = (formValues: any, index: number) => {
    if (!formValues?.name || !formValues?.value || !formValues?.date)
      return null;

    // Verifica se já é um objeto dayjs, se não for, converte
    const examDate = dayjs.isDayjs(formValues.date)
      ? formValues.date
      : dayjs(formValues.date);

    const uniqueId = `${formValues.name
      }-${examDate.valueOf()}-${Date.now()}-${index}`;

    return {
      id: uniqueId,
      item: [
        {
          text: "Nome do exame",
          answer: [{ valueString: formValues.name }],
        },
        {
          text: "Valor do resultado",
          answer: [
            {
              valueString: formValues.value,
            },
          ],
        },
        {
          text: "Data de realização",
          answer: [{ valueDate: examDate.format("YYYY-MM-DD") }],
        },
        {
          text: "Unidade",
          answer: [{ valueString: formValues.unit || "" }],
        },
      ],
      displayInfo: {
        healthcare: "Inserção Manual",
        practitioner: user?.name,
        identifier: `${index}-${uuid.v1()}`,
      },
      answerDate: dayjs().format(),
    };
  };

  const handleAddExtractedExam = (exams: ExtractedExam[]) => {
    const currentLength = newExams.length;

    // Create array of new inputs for all exams at once
    const newInputs = exams.map((_, index) => ({
      nameId: `newExames.${currentLength + index}.name`,
      valueId: `newExames.${currentLength + index}.value`,
      dateId: `newExames.${currentLength + index}.date`,
      unitId: `newExames.${currentLength + index}.unit`,
    }));

    // Set all new inputs at once
    setNewExams((prevExams: Array<NewExamField | string>) => [
      ...prevExams,
      ...newInputs,
    ]);

    // Set values for each exam using the correct index
    exams.forEach((exam, index) => {
      const examIndex = currentLength + index;
      setValue(`newExames.${examIndex}.name`, exam.name);
      setValue(`newExames.${examIndex}.value`, exam.value);
      setValue(`newExames.${examIndex}.date`, exam.date);
      setValue(`newExames.${examIndex}.unit`, exam.unit);
    });

    incrementInputKey();
  };

  const handleExamExtraction = async (selectedAnalytes: ExtractedAnalyte[]) => {
    const currentDate = dayjs();

    // Função auxiliar para processar componentes
    const processExam = (exam: any, category?: string): ExtractedExam[] => {
      if (exam.components) {
        // Se tem componentes, processa cada um
        return exam.components.map((component: any) => ({
          name: component.name,
          value: component.value,
          unit: component.unit || "",
          date: currentDate.format("MM/DD/YYYY"),
          category: category || exam.name,
        }));
      } else {
        // Se não tem componentes, retorna o próprio exame
        return [
          {
            name: exam.name,
            value: exam.value,
            unit: exam.unit || "",
            date: currentDate.format("MM/DD/YYYY"),
            category: category,
          },
        ];
      }
    };

    // Processa todos os exames e achata o array
    const results = selectedAnalytes.flatMap((exam) => processExam(exam));

    return {
      name: "Resultado de Exame",
      results,
    };
  };

  return {
    handleAddInput,
    handleAddExamForAnalyte,
    handleDeleteResult,
    formatNewExam,
    handleExamExtraction,
    handleAddExtractedExam,
    newExams,
    setValue,
  };
};
